// The event catalogue - every analytics event this app emits, named once here
// rather than as string literals scattered across screens.
//
// Two reasons it is a module and not a convention. Event names and their data
// keys become dashboard rows, so a typo does not fail, it just quietly creates
// a second event that looks like the first; and the funnel questions this
// integration exists to answer ("what did they do before signing up", "which
// button did they come in through", "which features do members use") are only
// answerable if the names on both sides of the sign-up boundary line up.
//
// Rules for anything added here:
//   - kebab-case names, matching umami's own convention.
//   - data values must be LOW-CARDINALITY - a fixed set of strings or a small
//     number. Never an id, a name, an email, a VIN or any customer detail.

import { track } from './analytics.js';

// ---------------------------------------------------------------------------
// Before sign-up
// ---------------------------------------------------------------------------

// The sample scan - fixed data, no backend at all (app/app/sampleData.js).
// 'landing' is the front page's simulated run; 'app' is /app/ro/sample, which
// a signed-in-but-unverified visitor reaches from Frontpage's banner.
export function trackSampleOrderViewed(surface) {
  track('sample-order-viewed', { surface });
}

// A real file dropped or picked on the front page - the anonymous demo
// extraction through services/tourguide.
export function trackDemoUploadStarted(via) {
  track('demo-upload-started', { via });
}

// 'done' | 'error' | 'quota'. 'quota' is the trial cap (TRIAL_QUOTA_EXCEEDED or
// a 402 from metering) - the outcome worth separating, since it is the one
// that is a product decision rather than a failure.
export function trackDemoUploadResult(outcome) {
  track('demo-upload-result', { outcome });
}

// "See More" - the full-quality reprocess of a demo scan.
export function trackFullDemoStarted() {
  track('full-demo-started');
}

// The full preview actually rendered - /tour/ro/:id. Distinct from
// full-demo-started, which only says they asked.
export function trackFullDemoViewed() {
  track('full-demo-viewed');
}

// ---------------------------------------------------------------------------
// Sign-up attribution
// ---------------------------------------------------------------------------

// Every button that starts a registration, one value each. Keep in step with
// the call sites - a source that appears in the data but not here is a call
// site someone typed a literal into.
export const SIGNUP_SOURCE = {
  LANDING_HEADER: 'landing-header',
  LANDING_TRIAL_BOX: 'landing-trial-box',
  LANDING_FINAL_CTA: 'landing-final-cta',
  TOUR_RO_HEADER: 'tour-ro-header',
  TOUR_WAIT_HEADER: 'tour-wait-header',
  SIGN_IN_NO_ACCOUNT: 'sign-in-no-account',
  SUBSCRIBE_PAGE: 'subscribe-page'
};

// The click and the completed registration are separated by a navigation and,
// on the resume path, by an entire round trip through an email inbox - so the
// source has to outlive the component that recorded it.
//
// sessionStorage rather than a module variable (a hard navigation or a magic
// link loses a module variable) and rather than localStorage (an attribution
// that survives until next month would attach a stale button to a fresh
// signup). Per-tab, for the life of the tab, is exactly the right window.
const SIGNUP_SOURCE_KEY = 'wu.signup.source';

// Records the click AND remembers it for signup-completed.
export function trackSignupCta(source, data) {
  track('signup-cta', { source, ...data });
  try {
    sessionStorage.setItem(SIGNUP_SOURCE_KEY, source);
  } catch {
    // Private mode, storage disabled: the CTA event above is still recorded.
    // Attribution on the completion event is the part that degrades, and
    // losing it must not cost the click.
  }
}

// 'direct' means they arrived at /sign-up without pressing any button we
// track - a bookmark, a typed URL, an external link. A real answer, not a gap.
export function readSignupSource() {
  try {
    return sessionStorage.getItem(SIGNUP_SOURCE_KEY) || 'direct';
  } catch {
    return 'unknown';
  }
}

function clearSignupSource() {
  try {
    sessionStorage.removeItem(SIGNUP_SOURCE_KEY);
  } catch {
    /* nothing to do */
  }
}

// Step 1 creates the account (passwordless); step 2 sets the password. They
// are separately abandonable, which is the whole reason to record both.
export function trackSignupStepCompleted(step) {
  track('signup-step-completed', { step, source: readSignupSource() });
}

export function trackSignupCompleted() {
  track('signup-completed', { source: readSignupSource() });
  // The registration is over; a later one in this tab is a different story.
  clearSignupSource();
}

// ---------------------------------------------------------------------------
// Features used by registered members
// ---------------------------------------------------------------------------

// 'card' is the repair-order screen's inline operation card; 'dialog' is the
// Review modal. The same action from two surfaces, deliberately one event with
// a discriminator rather than two events - the interesting number is how the
// split moves, which two separate events make you compute by hand.
export function trackCopyTechNotes(surface) {
  track('copy-tech-notes', { surface });
}

export function trackCopyCustomerSummary(surface) {
  track('copy-customer-summary', { surface });
}

export function trackReviewDialogOpened() {
  track('review-dialog-opened');
}

// A flagged OCR term resolved. 'edited' means the text was changed; 'confirmed'
// means it was accepted as-is. `duplicates` is how many other pending marks
// the same edit was applied to in one go (the bulk-apply confirm), 0 normally.
export function trackCorrectionApplied(outcome, duplicates = 0) {
  track('correction-applied', { outcome, duplicates });
}

// The "just let me rewrite freely" escape hatch on the notes editor.
export function trackFullEditToggled(enabled) {
  track('full-edit-toggled', { enabled });
}

// The scan-excerpt magnifier in the Review modal. Fired once per gesture, on
// activation - it is driven by pointermove, and one event per move would be
// thousands per visit.
export function trackExcerptMagnified() {
  track('excerpt-magnified');
}

// The full order list (/app/orders), as distinct from Frontpage's recent-
// orders widget.
export function trackOrderListViewed(count) {
  track('order-list-viewed', { count });
}
